import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck, HiPlus, HiTrash, HiDownload, HiUpload } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { getApiUrl } from "../../../config/env";
import { showSuccessToast, showErrorToast, showInfoToast } from "../../../utils/toastNotifications";
import "./EditShippingCompany.scss";

export const EditShippingCompany: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['shippingCompanies', 'common']);
  const { token } = useAuth();

  const [name, setName] = useState("");
  const [maxWeight, setMaxWeight] = useState(0);
  const [maxDiemention, setMaxDiemention] = useState(0);
  const [dwDivisot, setDwDivisot] = useState(0);
  const [insuranceRate, setInsuranceRate] = useState(0);
  const [fuelSurchargeRate, setFuelSurchargeRate] = useState(0);
  const [calculationBase, setCalculationBase] = useState(0); // 0 = PricePerPound, 1 = PricePerTotal
  const [clearance, setClearance] = useState<number | null>(null);
  const [dgFees, setDgFees] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [taxProfileId, setTaxProfileId] = useState<number | null>(null);
  const [taxProfiles, setTaxProfiles] = useState<any[]>([]);
  const [formData, setFormData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const keyCounter = useRef(0);
  const nextKey = () => ++keyCounter.current;
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-filter active source and destination countries once using useMemo to avoid performance issues in table rows
  const activeSourceCountries = useMemo(() => {
    return countries?.filter((c: any) => (c.countryType === 0 || c.countryType === 2) && !c.isSuspended) || [];
  }, [countries]);

  const activeDestinationCountries = useMemo(() => {
    return countries?.filter((c: any) => (c.countryType === 0 || c.countryType === 1) && !c.isSuspended) || [];
  }, [countries]);

  // Helper functions to get country option lists based on type, suspension status, and selection
  const getSourceCountryOptions = (selectedId?: string | number) => {
    const options = [...activeSourceCountries];
    if (selectedId && !options.some((c: any) => c.id == selectedId)) {
      const selected = countries.find((c: any) => c.id == selectedId);
      if (selected) {
        options.push(selected);
      }
    }
    return options.map((c: any) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ));
  };

  const getDestinationCountryOptions = (selectedId?: string | number) => {
    const options = [...activeDestinationCountries];
    if (selectedId && !options.some((c: any) => c.id == selectedId)) {
      const selected = countries.find((c: any) => c.id == selectedId);
      if (selected) {
        options.push(selected);
      }
    }
    return options.map((c: any) => (
      <option key={c.id} value={c.id}>
        {c.name}
      </option>
    ));
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await adminApi.call('/country', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('shippingCompanies:messages.fetchError'));
          return;
        }

        setCountries(response.data?.items);
      } catch (error: any) {
        showErrorToast(t('shippingCompanies:messages.fetchError'));
      }
    };

    const fetchCompany = async () => {
      try {
        const response = await adminApi.call(`/shippingcompany/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || "Error fetching shipping company.");
          return;
        }

        const data = response.data;
        setName(data.name);
        setMaxWeight(data.maxWeight);
        setMaxDiemention(data.maxDiemention);
        setDwDivisot(data.dwDivisot);
        setFuelSurchargeRate(data.fuelSurchargeRate);
        setCalculationBase(data.calculationBase ?? 0);
        setClearance(data.clearance ?? null);
        setInsuranceRate(data.insuranceRate);
        setDgFees(data.dgFees || 0);
        setIsActive(data.isActive);
        setTaxProfileId(data.taxProfileId ?? null);
        setFormData(
          data.shippingCompanyPrices.map((item) => ({
            ...item,
            key: nextKey(),
          }))
        );
      } catch (error) {
        showErrorToast("Error fetching shipping company.");
      }
    };

    fetchCountries();
    fetchCompany();
    adminApi.call<any[]>('/taxprofile', 'GET').then(response => {
      if (response.success) setTaxProfiles(response.data || []);
    });
  }, [id]);

  const handleInputChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...formData];
    updated[index][name] =
      name.includes("Rate") ||
        name.includes("Price") ||
        name.includes("Range") ||
        name === "dwDivisot"
        ? parseFloat(value)
        : parseInt(value);
    setFormData(updated);
  };

  const handleCompanyFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "name") setName(value);
    else if (name === "maxWeight") setMaxWeight(Number(value));
    else if (name === "maxDiemention") setMaxDiemention(Number(value));
    else if (name === "dwDivisot") setDwDivisot(Number(value));
    else if (name === "insuranceRate") setInsuranceRate(Number(value));
    else if (name === "fuelSurchargeRate") setFuelSurchargeRate(Number(value));
    else if (name === "dgFees") setDgFees(Number(value));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!name.trim()) newErrors.name = t('shippingCompanies:validation.nameRequired');
    if (!maxWeight || maxWeight <= 0) newErrors.maxWeight = t('shippingCompanies:validation.maxWeightRequired');
    if (!maxDiemention || maxDiemention <= 0) newErrors.maxDiemention = t('shippingCompanies:validation.maxDimensionRequired');
    if (!dwDivisot || dwDivisot <= 0) newErrors.dwDivisot = t('shippingCompanies:validation.dwDivisorRequired');
    if (!taxProfileId) newErrors.taxProfileId = 'Tax profile is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addNewRow = () => {
    setFormData([
      ...formData,
      {
        key: nextKey(),
        maxWeightRange: 0,
        normalFastPrice: 0,
        normalEconomyPrice: 0,
        dangerousFastPrice: 0,
        dangerousEconomyPrice: 0,
        sourceCountryId: 0,
        destinationCountryId: 0,
      },
    ]);
  };

  const deleteRow = (key) => {
    setFormData(formData.filter((item) => item.key !== key));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showErrorToast(t('shippingCompanies:validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/shippingcompany/admin/update', 'PUT', {
        id,
        name,
        maxWeight,
        maxDiemention,
        dwDivisot,
        insuranceRate,
        fuelSurchargeRate,
        calculationBase,
        clearance,
        dgFees,
        isActive,
        taxProfileId,
        shippingCompanyPrices: formData,
      });

      if (!response.success) {
        showErrorToast(response.error || t('shippingCompanies:messages.updateError'));
        return;
      }

      showSuccessToast(t('shippingCompanies:messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const url = getApiUrl(`/shippingcompany/${id}/prices/template`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        showErrorToast(t('shippingCompanies:messages.templateDownloadError'));
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${name || 'shipping'}_prices_template.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      showSuccessToast(t('shippingCompanies:messages.templateDownloadSuccess'));
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.templateDownloadError'));
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showErrorToast(t('shippingCompanies:messages.invalidFileType'));
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setImporting(true);

    try {
      const url = getApiUrl(`/shippingcompany/${id}/prices/import`);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        let parsedError: any = null;
        try {
          parsedError = await response.json();
        } catch {
          parsedError = null;
        }

        if (parsedError?.errors && Array.isArray(parsedError.errors)) {
          parsedError.errors.forEach((err: string) => showErrorToast(err));
        }

        showErrorToast(parsedError?.message || t('shippingCompanies:messages.importError'));
        return;
      }

      const result = await response.json();

      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach((err: string) => showErrorToast(err));
      }

      if (result.prices && result.prices.length > 0) {
        // Map imported prices into the form data format with unique keys
        const importedRows = result.prices.map((price: any) => ({
          ...price,
          key: nextKey(),
        }));

        // Replace existing prices with imported ones
        setFormData(importedRows);
        showSuccessToast(
          t('shippingCompanies:messages.importReplaceSuccess', { count: result.totalParsed })
        );
      } else if (!result.errors || result.errors.length === 0) {
        showInfoToast(t('shippingCompanies:messages.importEmpty'));
      }
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.importError'));
    } finally {
      setImporting(false);
      // Reset file input so re-uploading the same file works
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const breadcrumbs = [
    { label: t('shippingCompanies:breadcrumbs.shippingCompanies'), path: '/shipping-companies' },
    { label: t('shippingCompanies:breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={`${t('shippingCompanies:editShippingCompany')} - ${name}`}
      subtitle={t('shippingCompanies:subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={name}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.name')}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.maxWeight')} <span className="required">*</span></label>
            <input
              step={0.01}
              type="number"
              className={`form-control ${errors.maxWeight ? 'is-invalid' : ''}`}
              name="maxWeight"
              value={maxWeight}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.maxWeight')}
              min="0"
              required
            />
            {errors.maxWeight && <div className="invalid-feedback">{errors.maxWeight}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Tax Profile</label>
            <select className={`form-select ${errors.taxProfileId ? 'is-invalid' : ''}`} value={taxProfileId ?? ''} onChange={(e) => { setTaxProfileId(e.target.value ? Number(e.target.value) : null); setErrors(prev => ({ ...prev, taxProfileId: '' })); }} required>
              <option value="">Select a tax profile</option>
              {taxProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
            </select>
            {errors.taxProfileId && <div className="invalid-feedback">{errors.taxProfileId}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.maxDimension')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.maxDiemention ? 'is-invalid' : ''}`}
              name="maxDiemention"
              value={maxDiemention}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.maxDimension')}
              min="0"
              required
            />
            {errors.maxDiemention && <div className="invalid-feedback">{errors.maxDiemention}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.fuelSurchargeRate')}</label>
            <input
              type="number"
              className="form-control"
              value={fuelSurchargeRate}
              onChange={(e) => setFuelSurchargeRate(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.fuelSurchargeRate')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Calculation Base</label>
            <select
              className="form-select"
              value={calculationBase}
              onChange={(e) => setCalculationBase(Number(e.target.value))}
            >
              <option value={0}>Price Per Pound (× weight)</option>
              <option value={1}>Price Per Total (flat price)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Clearance Fee ($)</label>
            <input
              step={0.01}
              type="number"
              className="form-control"
              value={clearance ?? ''}
              onChange={(e) => setClearance(e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Enter clearance fee (optional)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.dwDivisor')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.dwDivisot ? 'is-invalid' : ''}`}
              name="dwDivisot"
              value={dwDivisot}
              onChange={handleCompanyFieldChange}
              placeholder={t('shippingCompanies:placeholders.dwDivisor')}
              min="0"
              required
            />
            {errors.dwDivisot && <div className="invalid-feedback">{errors.dwDivisot}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.insuranceRate')}</label>
            <input
              type="number"
              className="form-control"
              value={insuranceRate}
              onChange={(e) => setInsuranceRate(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.insuranceRate')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('shippingCompanies:fields.dgFees')}</label>
            <input
              type="number"
              className="form-control"
              value={dgFees}
              onChange={(e) => setDgFees(Number(e.target.value))}
              placeholder={t('shippingCompanies:placeholders.dgFees')}
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="form-label d-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="form-check-input"
              />
              {t('common:active')}
            </label>
          </div>
        </div>

        <div className="pricing-section">
          <div className="section-header">
            <h5>{t('shippingCompanies:fields.pricingTable')}</h5>
            <div className="section-header-actions">
              <ActionButton
                variant="secondary"
                icon={HiDownload}
                onClick={handleDownloadTemplate}
                size="sm"
              >
                {t('shippingCompanies:buttons.downloadTemplate')}
              </ActionButton>
              <ActionButton
                variant="secondary"
                icon={HiUpload}
                onClick={handleImportClick}
                size="sm"
                loading={importing}
                disabled={importing}
              >
                {importing
                  ? t('shippingCompanies:buttons.importing')
                  : t('shippingCompanies:buttons.importFromExcel')
                }
              </ActionButton>
              <ActionButton
                variant="success"
                icon={HiPlus}
                onClick={addNewRow}
                size="sm"
              >
                {t('shippingCompanies:buttons.addPriceEntry')}
              </ActionButton>
            </div>
          </div>

          {/* Hidden file input for Excel upload */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
          />

          <div className="pricing-table">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>{t('shippingCompanies:table.maxWeight')}</th>
                  <th>{t('shippingCompanies:table.normalFast')}</th>
                  <th>{t('shippingCompanies:table.normalEconomy')}</th>
                  <th>{t('shippingCompanies:table.dangerousFast')}</th>
                  <th>{t('shippingCompanies:table.dangerousEconomy')}</th>
                  <th>{t('shippingCompanies:table.sourceCountry')}</th>
                  <th>{t('shippingCompanies:table.destinationCountry')}</th>
                  <th>{t('shippingCompanies:table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {formData.map((item, index) => (
                  <tr key={item.key}>
                    <td>
                      <input
                        type="number"
                        step={0.01}
                        className="form-control"
                        name="maxWeightRange"
                        value={item.maxWeightRange}
                        onChange={(e) => handleInputChange(index, e)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step={0.01}
                        className="form-control"
                        name="normalFastPrice"
                        value={item.normalFastPrice}
                        onChange={(e) => handleInputChange(index, e)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step={0.01}
                        className="form-control"
                        name="normalEconomyPrice"
                        value={item.normalEconomyPrice}
                        onChange={(e) => handleInputChange(index, e)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step={0.01}
                        className="form-control"
                        name="dangerousFastPrice"
                        value={item.dangerousFastPrice}
                        onChange={(e) => handleInputChange(index, e)}
                      />
                    </td>
                    <td>
                      <input
                        step={0.01}
                        type="number"
                        className="form-control"
                        name="dangerousEconomyPrice"
                        value={item.dangerousEconomyPrice}
                        onChange={(e) => handleInputChange(index, e)}
                      />
                    </td>
                    <td>
                      <select
                        className="form-select"
                        name="sourceCountryId"
                        value={item.sourceCountryId}
                        onChange={(e) => handleInputChange(index, e)}
                      >
                        <option value="">Select</option>
                        {getSourceCountryOptions(item.sourceCountryId)}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        name="destinationCountryId"
                        value={item.destinationCountryId}
                        onChange={(e) => handleInputChange(index, e)}
                      >
                        <option value="">Select</option>
                        {getDestinationCountryOptions(item.destinationCountryId)}
                      </select>
                    </td>
                    <td>
                      <ActionButton
                        variant="danger"
                        size="sm"
                        icon={HiTrash}
                        onClick={() => deleteRow(item.key)}
                        tooltip={t('common:delete')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('shippingCompanies:buttons.updating') : t('shippingCompanies:buttons.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
